import { useClientUser } from '@/hooks';
import { createContext, useMemo, useState } from 'react';
import { UserContext } from '../App';
import { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { GET_CLAIMS } from './graphql';
import { captureException } from '@sentry/react';
import { GET_CARRIERS, GET_SHIPPERS } from '../admin/AdminExceptions/graphql';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const { admin_clients } = useContext(UserContext);
    const { user_id, isOnwardAdmin } = useClientUser();
    const [filters, setFilters] = useState({});
    const [notification, setNotification] = useState({});
    const [newClaim, setNewClaim] = useState(null);

    const where = useMemo(() => {
        let conditions = [];

        if (isOnwardAdmin) {
            if (filters.shipper) {
                conditions.push({ shipper_id: { _eq: filters.shipper } });
            }
            if (filters.carrier) {
                conditions.push({
                    _or: [
                        { carrier_id: { _eq: filters.carrier } },
                        { middle_mile_carrier_id: { _eq: filters.carrier } },
                    ],
                });
            }
            conditions.push({ order: { oms: { _eq: false } } });
        } else {
            const user_ids = user_id ? [user_id] : (admin_clients || []).map((client) => client.client_id);
            conditions.push({ _or: [{ shipper_id: { _in: user_ids } }, { carrier_id: { _in: user_ids } }] });
        }

        if (filters.startDate) {
            conditions.push({ incident_date: { _gte: filters.startDate } });
        }

        if (filters.endDate) {
            conditions.push({ incident_date: { _lt: filters.endDate } });
        }

        if (filters.claimType) {
            conditions.push({ claim_type: { _eq: filters.claimType } });
        }

        if (filters.claimStatus && filters.claimStatus !== 'ALL') {
            conditions.push({ claim_status: { _eq: filters.claimStatus } });
        }

        if (filters.search) {
            conditions.push({
                _or: [
                    { claim_number: { _ilike: `%${filters.search}%` } },
                    { order: { order_number: { _ilike: `%${filters.search}%` } } },
                    { order: { po_number: { _ilike: `%${filters.search}%` } } },
                ],
            });
        }

        return { _and: conditions };
    }, [user_id, isOnwardAdmin, admin_clients, filters]);

    const {
        data,
        loading: claimsLoading,
        refetch,
    } = useQuery(GET_CLAIMS, {
        variables: {
            where,
        },
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({
                severity: 'error',
                message: `Failed to load claims. ${e.message}`,
            });
        },
    });

    const claims = useMemo(() => {
        return data?.claims || [];
    }, [data]);

    const { data: shipperData, loading: shipperLoading } = useQuery(GET_SHIPPERS, {
        skip: !isOnwardAdmin,
        variables: {
            where: {
                oms: {
                    _eq: false,
                },
            },
        },
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({
                severity: 'error',
                message: `Failed to load shippers. ${e.message}`,
            });
        },
    });

    const { data: carrierData, loading: carrierLoading } = useQuery(GET_CARRIERS, {
        skip: !isOnwardAdmin,
        variables: {
            where: {
                oms: {
                    _eq: false,
                },
            },
        },
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({
                severity: 'error',
                message: `Failed to load carriers. ${e.message}`,
            });
        },
    });

    const shippers = useMemo(() => {
        return (shipperData?.results || [])
            .map((result) => result.order_shipper)
            .filter((shipper) => !!shipper?.client_id)
            .sort((a, b) => a.business_name.localeCompare(b.business_name));
    }, [shipperData]);

    const carriers = useMemo(() => {
        return (carrierData?.results || [])
            .map((result) => result.order_carrier)
            .filter((carrier) => !!carrier?.client_id)
            .sort((a, b) => a.business_name.localeCompare(b.business_name));
    }, [carrierData]);

    return (
        <Context.Provider
            value={{
                state: {
                    filters,
                    claims,
                    shippers,
                    carriers,
                    notification,
                    newClaim,
                    loading: {
                        claims: claimsLoading,
                        shippers: shipperLoading,
                        carriers: carrierLoading,
                    },
                },
                callbacks: {
                    filter: (key, value) => setFilters((prev) => ({ ...prev, [key]: value })),
                    setNotification,
                    setNewClaim,
                    refetch,
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
